import { Box, Button, Skeleton, TextField, Typography } from '@mui/material';

import { useT } from '@/hooks';

const MONO_FONT = 'var(--font-plex-mono), IBM Plex Mono, monospace';
const HEADING_FONT = 'var(--font-archivo), Archivo, sans-serif';
const quickButtonSx = {
  flex: 1,
  fontFamily: MONO_FONT,
  fontWeight: 500,
  fontSize: '0.78125rem',
  py: 1.125,
  borderColor: 'divider',
  color: 'text.secondary',
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    color: 'text.primary',
  },
} as const;

interface SharesInputProps {
  shares: string;
  activeTab: 'buy' | 'sell';
  isAuthenticated: boolean;
  userHolding: number;
  sharesNum: number;
  isLoadingHolding?: boolean;
  onSharesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickAmount: (amount: number) => void;
  onSetShares: (value: string) => void;
}

export function SharesInput({
  shares,
  activeTab,
  isAuthenticated,
  userHolding,
  sharesNum,
  isLoadingHolding = false,
  onSharesChange,
  onQuickAmount,
  onSetShares,
}: SharesInputProps) {
  const { t } = useT();
  return (
    <Box sx={{ mb: 2.25 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.125 }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('trade.shares', { defaultValue: 'Shares' })}
        </Typography>
        {activeTab === 'sell' && isAuthenticated && (
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'text.disabled',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {t('trade.availableShares', { defaultValue: 'Available Shares:' })}{' '}
            {isLoadingHolding ? (
              <Skeleton
                variant="text"
                width={48}
                height={16}
                sx={{ display: 'inline-block', borderRadius: '4px' }}
              />
            ) : (
              userHolding.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 6,
              })
            )}
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        value={shares}
        onChange={onSharesChange}
        sx={{
          mb: activeTab === 'sell' && sharesNum > 0 ? 0.75 : 1.25,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            borderRadius: '10px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '& input': {
            textAlign: 'center',
            fontFamily: MONO_FONT,
            fontWeight: 600,
            fontSize: '0.875rem',
            py: 1.125,
          },
        }}
      />
      {activeTab === 'buy' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(-100)}
            sx={quickButtonSx}
          >
            -100
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(-10)}
            sx={quickButtonSx}
          >
            -10
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(10)}
            sx={quickButtonSx}
          >
            +10
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(100)}
            sx={quickButtonSx}
          >
            +100
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(200)}
            sx={{
              ...quickButtonSx,
              color: 'secondary.main',
              '&:hover': { ...quickButtonSx['&:hover'], borderColor: 'secondary.main' },
            }}
          >
            +200
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const val = userHolding * 0.25;
              onSetShares(val.toString());
            }}
            sx={quickButtonSx}
          >
            25%
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const val = userHolding * 0.5;
              onSetShares(val.toString());
            }}
            sx={quickButtonSx}
          >
            50%
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              onSetShares(userHolding.toString());
            }}
            sx={quickButtonSx}
          >
            {t('trade.max', { defaultValue: 'Max' })}
          </Button>
        </Box>
      )}
    </Box>
  );
}
